import * as yup from 'yup';
import { Trash2 } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DiscountStrategy, DiscountType, GetDiscountQuery as GetDiscount, ProductType } from '__generated__/graphql';
import { TranslatableInput } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DateRangeForm, { DateTimeForm, dateRangeObject } from '../../../Common/DateRangeForm';
import DiscountValueInput from './DiscountValueInput';
import EditDiscountMutation from './EditDiscountMutation';
import ProductSelectionForm from '../../../Common/ProductSelectionForm';
import TicketSelectionForm from '../../../Common/TicketSelectionForm';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useMutation from '../../../../api/useMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Discount = GetDiscount['discount'];
type Event = GetDiscount['event'];

const getDiscountFormSchema = (strategy: DiscountStrategy) => yup.object().shape({
  title: yup.string().required(),
  ...dateRangeObject(false, false),
  levels: yup.array(yup.object({
    value: yup.number().required().min(0),
    ...strategy === DiscountStrategy.scheduled && {
      end_date: yup.string().required(),
    },
    ...strategy === DiscountStrategy.volume && {
      quantity: yup.number().required().min(1),
    },
  })).min(1),
});

export interface DiscountLevelData {
  quantity: number;
  value: number;
}

export interface EditDiscountFormProps {
  discount: Discount;
  event: Event;
}

const EditDiscountForm = ({ discount, event }: EditDiscountFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();
  const integerInput = useIntegerInput();
  const { scrollToIfInvisible } = useScroll();

  const getValues = (discount: Discount) => ({
    id: discount.id,
    strategy: discount.strategy,
    type: discount.type,
    title: discount.title,
    title_translations: translationsForAttribute(discount.translations, 'title'),
    start_date: discount.start_date,
    end_date: discount.end_date,
    tickets: discount.tickets.map(({ id }) => ({ id })),
    products: discount.products.map(({ id }) => ({ id })),
    levels: [
      ...discount.levels.map((level) => ({
        quantity: level.quantity,
        end_date: level.end_date,
        value: level.value,
      })),
      ...discount.levels.length === 0 ? [{ quantity: null, end_date: null, value: null }] : [],
    ],
  });

  const form = useForm({
    values: getValues(discount),
  });

  useNavigationPrompt(form.changed());

  const levelKeys = useRef(form.values.levels.map((value, index) => index));

  const [editDiscount, { error, loading }] = useMutation(EditDiscountMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ editDiscount: discount }) => {
      notifier.success(t('notifications.discount_saved'));

      form.reset(getValues(discount));

      // Update keys to rerender all levels, because the order of levels might have been updated by the server.
      const maxKey = Math.max(0, ...levelKeys.current);
      levelKeys.current = levelKeys.current.map((key) => key + maxKey + 1);
    },
    onError: () => {
      scrollToIfInvisible(`DiscountCard_${discount.id}`);
    },
  });

  const { tickets } = event;
  const products = event.products
    .filter((product) => product.type === ProductType.item || product.type === ProductType.fee);

  const setType = (type: DiscountType) => {
    form.set((values) => ({
      ...values,
      type,
      levels: values.levels.map((level) => {
        if (level.value && type !== values.type) {
          if (type === DiscountType.absolute) {
            level.value *= 100;
          } else if (type === DiscountType.percentage) {
            level.value = Math.min(Math.round(level.value / 100), 100);
          }
        }

        return { ...level };
      }),
    }));
  };

  const addLevel = () => {
    form.append('levels', {
      quantity: null,
      end_date: null,
      value: null,
    });
    levelKeys.current.push(Math.max(0, ...levelKeys.current) + 1);
  };

  const deleteLevel = (index: number) => {
    form.delete('levels', index);
    levelKeys.current.splice(index, 1);
  };

  const { valid, errors } = useValidation({
    schema: getDiscountFormSchema(form.values.strategy),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Div id={`EditDiscountForm_${discount.id}`}>
      <UI.Form onSubmit={() => editDiscount()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('basic_info')}
            </UI.Legend>
            <UI.InputDescription>
              {t('discount_basic_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
            <UI.InputLabel htmlFor="DiscountTitle">
              {t('discount_title')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <TranslatableInput
              form={form}
              errors={errors}
              name="title"
              defaultLocale={discount.locale}
              title={t('discount_title')}
              placeholder={t('discount_title_placeholder')}
              id="DiscountTitle"
            />
            <UI.ErrorMessages attribute={t('discount_title')} errors={errors.title} />
          </UI.InputGroup>

          <UI.FormGrid sc={{ columns: [undefined, '2fr 2fr 32px'], gutter: 1 }}>
            <UI.InputGroup sc={{ valid: !errors.strategy, touched: form.touched.strategy }}>
              <UI.InputLabel>
                {t('discount_strategy')}
              </UI.InputLabel>
              <UI.RadioPill>
                <UI.Radio
                  onChange={() => form.set('strategy', DiscountStrategy.scheduled)}
                  checked={form.values.strategy === DiscountStrategy.scheduled}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('scheduled_discount')}
                </UI.Radio>
                <UI.Radio
                  onChange={() => form.set('strategy', DiscountStrategy.volume)}
                  checked={form.values.strategy === DiscountStrategy.volume}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('volume_discount')}
                </UI.Radio>
              </UI.RadioPill>
              <UI.ErrorMessages
                attribute={t('discount_strategy')}
                errors={errors.strategy}
              />
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.type, touched: form.touched.type }}>
              <UI.InputLabel>
                {t('discount_type')}
              </UI.InputLabel>
              <UI.RadioPill>
                <UI.Radio
                  onChange={() => setType(DiscountType.absolute)}
                  checked={form.values.type === DiscountType.absolute}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('absolute_amount')}
                </UI.Radio>
                <UI.Radio
                  onChange={() => setType(DiscountType.percentage)}
                  checked={form.values.type === DiscountType.percentage}
                  sc={{ box: true, fontWeight: 500 }}
                >
                  {t('percentage_amount')}
                </UI.Radio>
              </UI.RadioPill>
              <UI.ErrorMessages
                attribute={t('discount_type')}
                errors={errors.type}
              />
            </UI.InputGroup>
          </UI.FormGrid>

          <UI.FormGrid sc={{ gutter: 0.5 }}>
            <UI.FormGrid sc={{ columns: ['1fr 1fr 32px', '2fr 2fr 32px'], gutter: 1 }}>
              {form.values.strategy === DiscountStrategy.scheduled && (
                <UI.InputGroup
                  sc={{
                    valid: form.values.levels.filter((value, index) => errors[`levels.${index}.end_date`]).length === 0,
                    touched: form.getTouched(`levels.${form.values.levels.length - 1}.end_date`),
                  }}
                >
                  <UI.InputLabel htmlFor="levels.0.end_date" sc={{ mb: 0 }}>
                    {t('valid_until')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                </UI.InputGroup>
              )}
              {form.values.strategy === DiscountStrategy.volume && (
                <UI.InputGroup
                  sc={{
                    valid: form.values.levels.filter((value, index) => errors[`levels.${index}.quantity`]).length === 0,
                    touched: form.getTouched(`levels.${form.values.levels.length - 1}.quantity`),
                  }}
                >
                  <UI.InputLabel htmlFor="DiscountQuantity_0" sc={{ mb: 0 }}>
                    {t('minimum_purchase_quantity')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                </UI.InputGroup>
              )}
              <UI.InputGroup
                sc={{
                  valid: form.values.levels.filter((value, index) => errors[`levels.${index}.value`]).length === 0,
                  touched: form.getTouched(`levels.${form.values.levels.length - 1}.value`),
                }}
              >
                <UI.InputLabel htmlFor="DiscountValue_levels0Value" sc={{ mb: 0 }}>
                  {t('discount_per_item')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
              </UI.InputGroup>
            </UI.FormGrid>
            {form.values.levels.map((level, index) => (
              <UI.FormGrid
                sc={{ columns: ['1fr 1fr 32px', '2fr 2fr 32px'], gutter: 1 }}
                key={levelKeys.current[index]}
              >
                {form.values.strategy === DiscountStrategy.scheduled && (
                  <UI.InputGroup
                    sc={{
                      valid: !errors[`levels.${index}.end_date`],
                      touched: form.getTouched(`levels.${index}.end_date`),
                    }}
                  >
                    <DateTimeForm
                      form={form}
                      errors={errors}
                      keys={{ date: `levels.${index}.end_date`, time: `levels.${index}.end_time` }}
                      labels={{ date: t('end_date'), time: t('time') }}
                      hideLabels
                      hideErrors
                      gutter={0.5}
                      require={false}
                    />
                    <UI.ErrorMessages
                      attribute={t('end_date')}
                      errors={errors[`levels.${index}.end_date`]}
                    />
                  </UI.InputGroup>
                )}
                {form.values.strategy === DiscountStrategy.volume && (
                  <UI.InputGroup
                    sc={{
                      valid: !errors[`levels.${index}.quantity`],
                      touched: form.getTouched(`levels.${index}.quantity`),
                    }}
                  >
                    <UI.InputWrapper>
                      <UI.DebouncedInput
                        {...form.text(`levels.${index}.quantity`, { inputHandler: integerInput() })}
                        format={integerInput().format}
                        maxLength={4}
                        inputMode="numeric"
                        match={/[\d]/g}
                        id={`DiscountQuantity_${index}`}
                      />
                      <UI.Span>
                        {t('items', { count: level.quantity })}
                      </UI.Span>
                    </UI.InputWrapper>
                    <UI.ErrorMessages
                      attribute={t('min_quantity')}
                      errors={errors[`levels.${index}.quantity`]}
                    />
                  </UI.InputGroup>
                )}
                <UI.InputGroup
                  sc={{
                    valid: !errors[`levels.${index}.value`],
                    touched: form.getTouched(`levels.${index}.value`),
                  }}
                >
                  <DiscountValueInput form={form} prefix={`levels.${index}`} />
                  <UI.ErrorMessages
                    attribute={t('discount')}
                    errors={errors[`levels.${index}.value`]}
                  />
                </UI.InputGroup>
                {form.values.levels.length > 1 && (
                  <UI.Div sc={{ pt: 1 }}>
                    <UI.Button
                      sc={{ blank: true, size: 'sm', color: 'gray.400' }}
                      onClick={() => deleteLevel(index)}
                    >
                      <UI.Icon>
                        <Trash2 />
                      </UI.Icon>
                    </UI.Button>
                  </UI.Div>
                )}
              </UI.FormGrid>
            ))}
            <UI.Div>
              <UI.Button onClick={() => addLevel()}>
                {t('add_level')}
              </UI.Button>
            </UI.Div>
          </UI.FormGrid>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('tickets_products')}
            </UI.Legend>
            <UI.InputDescription>
              {t('discount_tickets_products_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <TicketSelectionForm
              form={form}
              tickets={tickets}
              errors={errors}
              event={event}
              isDisabled={(ticket) => ticket.discounts.filter((item) => item.id !== discount.id).length > 0}
            />

            <ProductSelectionForm
              form={form}
              products={products}
              errors={errors}
              event={event}
              isDisabled={(product) => product.discounts.filter((item) => item.id !== discount.id).length > 0}
            />
          </UI.FormGrid>

          {discount.active && form.values.tickets.length === 0 && form.values.products.length === 0 && (
            <UI.Warning>
              {(t('discount_no_tickets_or_products_warning'))}
            </UI.Warning>
          )}

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('availability')}
            </UI.Legend>
            <UI.InputDescription>
              {t('discount_start_and_end_date')}
            </UI.InputDescription>
          </UI.Div>

          {form.values.strategy === DiscountStrategy.scheduled && (
            <DateTimeForm
              form={form}
              keys={{ date: 'start_date', time: 'start_time' }}
              labels={{ date: t('start_date'), time: t('start_time') }}
              errors={errors}
            />
          )}

          {form.values.strategy === DiscountStrategy.volume && (
            <DateRangeForm form={form} errors={errors} />
          )}

          <UI.HR />

          <UI.FormButtons
            form={form}
            submitLabel={t('save_discount')}
            closeTo={`/${project.id}/events/${event.id}/discounts`}
            loading={loading}
            valid={valid}
          />
        </UI.FormGrid>
      </UI.Form>
    </UI.Div>
  );
};

export default EditDiscountForm;
